import { alpha, Box, Chip, Stack, Tooltip, useTheme } from '@mui/material';
import React from 'react';

type ChipColor = 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';

type Props = {
    label: string;
    count: number;
    color: ChipColor;
    selected: boolean;
    onClick: () => void;
    icon?: React.ReactElement;
    tooltip?: string;
};

/**
 * Pill-shaped filter chip with an inline count badge. Selected chips are filled with the palette colour,
 * unselected chips are outlined on a faint tint of the same colour.
 */
const SextforceMetricsCountChip = ({ label, count, color, selected, onClick, icon, tooltip }: Props) => {
    const theme = useTheme();
    const main = theme.palette[color].main;
    const contrast = theme.palette[color].contrastText;

    const chip = (
        <Chip
            clickable
            onClick={onClick}
            icon={icon}
            aria-pressed={selected}
            label={
                <Stack direction="row" alignItems="center" spacing={1}>
                    <span>{label}</span>
                    <Box
                        component="span"
                        sx={{
                            px: 1,
                            minWidth: 28,
                            textAlign: 'center',
                            borderRadius: 999,
                            fontSize: '0.8125rem',
                            fontWeight: 600,
                            lineHeight: '22px',
                            color: selected ? contrast : main,
                            backgroundColor: selected ? alpha(contrast, 0.18) : alpha(main, 0.08),
                            border: `1px solid ${selected ? alpha(contrast, 0.45) : alpha(main, 0.4)}`,
                        }}
                    >
                        {count.toLocaleString()}
                    </Box>
                </Stack>
            }
            sx={{
                height: 44,
                borderRadius: 22,
                px: 0.75,
                fontSize: '1rem',
                fontWeight: 500,
                color: selected ? contrast : main,
                backgroundColor: selected ? main : alpha(main, 0.05),
                border: `1.5px solid ${main}`,
                '& .MuiChip-icon': { color: selected ? contrast : main, marginLeft: 1 },
                '&:hover, &:focus-visible': {
                    backgroundColor: selected ? theme.palette[color].dark : alpha(main, 0.12),
                },
                '&:focus-visible': { outline: `2px solid ${alpha(main, 0.5)}`, outlineOffset: 2 },
            }}
        />
    );

    return tooltip ? <Tooltip title={tooltip}>{chip}</Tooltip> : chip;
};

export default SextforceMetricsCountChip;
